import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Mic, Play, Radio, ExternalLink, Youtube,
  Headphones, Clock, Tv, Wifi
} from "lucide-react";
import { usePageSEO } from "@/hooks/usePageSEO";

const DISTRIBUTION_LINKS = [
  { name: "Spreaker", url: "https://www.spreaker.com/podcast/10-minutes-of-talk--5933432", color: "bg-orange-50 text-orange-700 border-orange-200" },
  { name: "Apple Podcasts", url: "https://podcasts.apple.com/us/podcast/10-minutes-of-talk/id552424353?uo=4", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { name: "Spotify", url: "https://open.spotify.com/show/3yXfSbL5ftBffkOT1hzshi", color: "bg-green-50 text-green-700 border-green-200" },
  { name: "iHeartRadio", url: "https://iheart.com/podcast/121731631", color: "bg-red-50 text-red-700 border-red-200" },
  { name: "Amazon Music", url: "https://music.amazon.com/podcasts/8c1cf2a0-025e-47c5-b176-5a73ad0b464d", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { name: "Deezer", url: "https://www.deezer.com/show/1000206185", color: "bg-pink-50 text-pink-700 border-pink-200" },
  { name: "Castbox", url: "https://castbox.fm/channel/id5564375", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { name: "Podcast Addict", url: "https://podcastaddict.com/podcast/10-minutes-of-talk/4590129", color: "bg-teal-50 text-teal-700 border-teal-200" },
  { name: "Podchaser", url: "https://www.podchaser.com/podcasts/10-minutes-of-talk-5933432", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
];

const YOUTUBE_PLAYLIST = "https://www.youtube.com/watch?v=Np-sKbTfdco&list=PLoGeha5n_c6tASgiYtlgR5Kwh2Np4kbND";

const EPISODES = [
  { id: 1, title: "Let's Get Serious in 2026 Workshop Live", description: "LIVE WORKSHOP: From Self-Employed to CEO-Level Leadership. Mindset and leadership shift for business owners to move from operators to strategic CEOs.", duration: "1h 31min", category: "Workshop", date: "Dec 17, 2025", url: "https://www.spreaker.com/episode/let-s-get-serious-in-2026-workshop-live--69104761" },
  { id: 2, title: "Emma S Lyons — Money Block Breakthrough Expert", description: "Uncover the subconscious patterns that quietly kill momentum, confidence, and cash flow with Money Block Breakthrough Expert Emma S. Lyons.", duration: "14 min", category: "Interview", date: "Jun 5, 2025", url: "https://www.spreaker.com/episode/emma-s-lyons-money-block-breakthrough-expert--66412012" },
  { id: 3, title: "Mastering the Media Game with Sarah Strackhouse", description: "CEO of Strackhouse Media shares her entrepreneurial journey, media training insights, and navigating gender gaps in AV production.", duration: "22 min", category: "Interview", date: "May 30, 2025", url: "https://www.spreaker.com/episode/mastering-the-media-game-with-sarah-strackhouse--66340396" },
  { id: 4, title: "Trauma Ain't Strategy — Unlearning the Hustle", description: "Licensed Therapist Janella D. Dobbs breaks down how unhealed emotional wounds impact business decisions and the Expensive Trauma framework.", duration: "55 min", category: "Business Strategy", date: "May 27, 2025", url: "https://www.spreaker.com/episode/trauma-ain-t-strategy-unlearning-the-hustle-with-nina-capone-janella-d-dobbs--66295725" },
  { id: 5, title: "Anti Elixir — Interview with CEO Ethan Boivie", description: "The CEO of Anti Elixir breaks down the science and mission behind the world's first drink designed to fight hangovers.", duration: "16 min", category: "Interview", date: "May 22, 2025", url: "https://www.spreaker.com/episode/anti-10-minutes-of-talk-interview-with-ceo-founder-ethan-boivie--66208431" },
  { id: 6, title: "BusinessPowerTools.com — CEO Burke Franklin", description: "Live interview unpacking tools, tactics, and real talk about building smarter, more efficient businesses.", duration: "39 min", category: "Interview", date: "May 13, 2025", url: "https://www.spreaker.com/episode/businesspowertools-com-ceo-burke-franklin--66075538" },
  { id: 7, title: "Love for Words CEO CaTyra Polland", description: "Powerhouse publisher and founder of National Black Authors Day shares her mission to amplify Black voices in publishing.", duration: "16 min", category: "Interview", date: "May 8, 2025", url: "https://www.spreaker.com/episode/love-for-words-ceo-catyra-polland-on-10-minutes-of-talk--66004490" },
  { id: 8, title: "How to Hire the Right Employees to Grow Your Business", description: "Key strategies for hiring employees that fuel growth and profitability — from A-players to performance-based pay.", duration: "22 min", category: "Business Strategy", date: "Mar 26, 2025", url: "https://www.spreaker.com/episode/how-to-hire-the-right-employees-to-grow-yout-business-and-increase-profits-10-minutes-of-talk-with-nina-capone--65136344" },
  { id: 9, title: "The Key to Small Business Survival", description: "Three key pillars that boost efficiency, cut wasted time, and keep your business thriving. With AI co-host Chris.", duration: "14 min", category: "Business Strategy", date: "Feb 26, 2025", url: "https://www.spreaker.com/episode/the-key-to-small-business-survival-season-4-episode-3--64587813" },
];

export const PodcastMediaPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  usePageSEO("10 Minutes of Talk - Business Podcast", "Quick, actionable business insights in 10 minutes or less. Available on Apple Podcasts, Spotify, iHeartRadio, Amazon Music, and more.");

  const categories = ["all", ...new Set(EPISODES.map(e => e.category))];

  return (
    <div className="min-h-screen bg-white" data-testid="podcast-media-page">
      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" />
            <span className="text-sm text-neutral-400">Podcast</span>
          </Link>
          <Button variant="ghost" className="text-neutral-300 hover:text-white" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#333333] via-neutral-800 to-[#333333] py-16 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border border-[#39FF14] rounded-full" />
          <div className="absolute bottom-10 right-20 w-48 h-48 border border-[#39FF14] rounded-full" />
        </div>
        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 text-[#39FF14] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Radio className="w-4 h-4" /> Now Streaming
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">10 Minutes of Talk</h1>
          <p className="text-neutral-300 text-lg max-w-2xl mx-auto mb-8">
            Quick, actionable business insights in 10 minutes or less. Hosted by My Biz Consulting
            and powered by In Da Streets Radio.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="https://www.spreaker.com/podcast/10-minutes-of-talk--5933432" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#39FF14] hover:bg-[#32E010] text-black font-medium">
                <Headphones className="w-5 h-5 mr-2" /> Listen on Spreaker
              </Button>
            </a>
            <a href={YOUTUBE_PLAYLIST} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-red-400 text-red-400 hover:bg-red-500/10">
                <Youtube className="w-5 h-5 mr-2" /> Watch on YouTube
              </Button>
            </a>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-12">

        {/* ── Listen Everywhere ── */}
        <section className="mb-14">
          <h2 className="text-xl font-bold text-neutral-900 mb-5 flex items-center gap-2">
            <Headphones className="w-5 h-5 text-[#2BC40E]" /> Listen Everywhere
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
            {DISTRIBUTION_LINKS.map((link) => (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center hover:shadow-md transition-all group ${link.color}`}
                data-testid={`dist-${link.name.toLowerCase().replace(/\s/g, '-')}`}>
                <ExternalLink className="w-4 h-4 mb-1.5 opacity-40 group-hover:opacity-100 transition-opacity" />
                <span className="text-[10px] font-medium leading-tight">{link.name}</span>
              </a>
            ))}
          </div>
        </section>

        {/* ── YouTube Podcast ── */}
        <section className="mb-14">
          <h2 className="text-xl font-bold text-neutral-900 mb-5 flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-600" /> Video Podcast
          </h2>
          <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-black">
            <div className="aspect-video">
              <iframe
                src="https://www.youtube.com/embed/videoseries?list=PLoGeha5n_c6tASgiYtlgR5Kwh2Np4kbND"
                title="10 Minutes of Talk - YouTube Playlist"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
          <p className="text-xs text-neutral-500 text-center mt-2">
            Full playlist on{" "}
            <a href={YOUTUBE_PLAYLIST} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">
              YouTube <ExternalLink className="w-3 h-3 inline" />
            </a>
          </p>
        </section>

        {/* ── Audio Podcast Embed ── */}
        <section className="mb-14">
          <h2 className="text-xl font-bold text-neutral-900 mb-5 flex items-center gap-2">
            <Mic className="w-5 h-5 text-[#2BC40E]" /> Audio Podcast
          </h2>
          <div className="rounded-2xl overflow-hidden border border-neutral-200">
            <iframe
              src="https://widget.spreaker.com/player?show_id=5933432&theme=light&playlist=show&playlist-hierarchical=true&chapters-image=true"
              style={{ width: "100%", height: "400px" }}
              title="10 Minutes of Talk - Spreaker Player"
              allow="autoplay"
            />
          </div>
        </section>

        {/* ── Live Stream ── */}
        <section className="mb-14">
          <h2 className="text-xl font-bold text-neutral-900 mb-5 flex items-center gap-2">
            <Wifi className="w-5 h-5 text-red-500" /> Live Stream
          </h2>
          <Card className="border-neutral-200 bg-neutral-50">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Tv className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">Live Streams Coming Soon</h3>
              <p className="text-sm text-neutral-600 max-w-md mx-auto mb-4">
                Live business Q&A sessions, interviews, and community conversations. 
                Links will be posted here when we go live.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-medium border border-red-200">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                No active stream — check back soon
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ── Episodes ── */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-5 flex items-center gap-2">
            <Play className="w-5 h-5 text-[#2BC40E]" /> Featured Episodes
          </h2>

          <div className="flex gap-2 mb-5 flex-wrap">
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === c ? "bg-[#333333] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
                {c === "all" ? "All Episodes" : c}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {EPISODES.filter(e => filter === "all" || e.category === filter).map((ep) => (
              <a key={ep.id} href={ep.url} target="_blank" rel="noopener noreferrer" className="block">
                <Card className="border-neutral-200 hover:shadow-md hover:border-[#39FF14]/30 transition-all group" data-testid={`episode-${ep.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#333333] flex items-center justify-center flex-shrink-0 group-hover:bg-[#39FF14]/20 transition-colors">
                        <Play className="w-5 h-5 text-[#39FF14]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-neutral-900 text-sm group-hover:text-[#2BC40E] transition-colors">{ep.title}</h3>
                          <Badge variant="outline" className="text-xs">{ep.category}</Badge>
                        </div>
                        <p className="text-xs text-neutral-600">{ep.description}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] text-neutral-400 flex items-center gap-1"><Clock className="w-3 h-3" />{ep.duration}</span>
                          <span className="text-[10px] text-neutral-400">{ep.date}</span>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-neutral-300 group-hover:text-[#2BC40E] transition-colors flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default PodcastMediaPage;
